"use client";

import { useRef, useState, useMemo } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import Script from "next/script";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Separator } from "@/components/ui/separator";
import { cn } from "@/lib/utils";
import { type ColorScheme, COLOR_SCHEMES } from "@/lib/colorSchemes";
import Image from "next/image";

// ---------------------------------------------------------------------------
// Marketo globals
// ---------------------------------------------------------------------------
declare global {
  interface Window {
    MktoForms2?: {
      loadForm: (
        baseUrl: string,
        munchkinId: string,
        formId: number,
        callback?: (form: MktoForm) => void,
      ) => void;
    };
  }
}

interface MktoForm {
  setValues: (values: Record<string, unknown>) => MktoForm;
  submit: () => void;
  onSuccess: (callback: () => boolean) => void;
}

// ---------------------------------------------------------------------------
// Public types
// ---------------------------------------------------------------------------
export interface ContactFormField {
  /** Marketo API name — e.g. "FirstName", "LastName", "Email", "Phone" */
  marketoName: string;
  /** Display label */
  label: string;
  /** HTML input type */
  type?: "text" | "email" | "tel";
  /** Placeholder text */
  placeholder?: string;
  /** Whether this field is required. Defaults to true. */
  required?: boolean;
}

export interface ContactFormProps {
  /** The Marketo form ID — e.g. 1234 */
  marketoFormId?: number;
  /** Fields to render. Defaults to 4-field set if not provided. */
  fields?: ContactFormField[];
  /** Optional success message after submission */
  successMessage?: string;
  /** Submit button label. Defaults to "Get started today". */
  ctaLabel?: string;
  /** When true, wraps the form in a rounded card container. */
  boxed?: boolean;
  /** Color scheme for the form — controls background and text colours. */
  colorScheme?: ColorScheme;
}

// ---------------------------------------------------------------------------
// Defaults
// ---------------------------------------------------------------------------
const DEFAULT_FIELDS: ContactFormField[] = [
  {
    marketoName: "FirstName",
    label: "First name",
    placeholder: "First Name",
    type: "text",
    required: true,
  },
  {
    marketoName: "LastName",
    label: "Last name",
    placeholder: "Last Name",
    type: "text",
    required: true,
  },
  {
    marketoName: "Email",
    label: "Work email",
    placeholder: "name@company.com",
    type: "email",
    required: true,
  },
  {
    marketoName: "Phone",
    label: "Phone number",
    placeholder: "(555) 123-4567",
    type: "tel",
    required: false,
  },
];

// ---------------------------------------------------------------------------
// Dynamic Zod schema
// ---------------------------------------------------------------------------
function buildSchema(fields: ContactFormField[]) {
  const shape: Record<string, z.ZodTypeAny> = {};
  for (const f of fields) {
    let rule: z.ZodString = z.string();
    if (f.type === "email") {
      rule = rule.email("Enter a valid email");
    }
    if (f.required !== false) {
      shape[f.marketoName] = rule.min(1, "Required");
    } else {
      shape[f.marketoName] = rule.optional();
    }
  }
  return z.object(shape);
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------
export function ContactForm({
  marketoFormId,
  fields,
  successMessage,
  ctaLabel = "Get started today",
  boxed = false,
  colorScheme,
}: ContactFormProps) {
  const resolvedFields = fields ?? DEFAULT_FIELDS;
  const scheme = colorScheme ? COLOR_SCHEMES[colorScheme] : undefined;
  const schema = useMemo(() => buildSchema(resolvedFields), [resolvedFields]);
  type FormData = z.infer<typeof schema>;

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  });

  const mktoFormRef = useRef<MktoForm | null>(null);
  const [submitted, setSubmitted] = useState(false);
  const [mktoReady, setMktoReady] = useState(false);

  const baseUrl = process.env.NEXT_PUBLIC_MARKETO_BASE_URL;
  const munchkinId = process.env.NEXT_PUBLIC_MARKETO_MUNCHKIN_ID;
  const canLoadMarketo = !!(baseUrl && munchkinId && marketoFormId);

  function initMarketo() {
    if (!canLoadMarketo || !window.MktoForms2) return;
    // Guard: Marketo's loadForm requires the hidden <form> DOM node to exist.
    // If it isn't mounted yet (e.g. fast script load), bail — the Script's
    // onLoad will not fire again, so this is safe; the form just won't submit
    // to Marketo, which is the correct silent-failure behaviour.
    if (!document.getElementById(`mktoForm_${marketoFormId}`)) return;
    window.MktoForms2.loadForm(
      baseUrl!,
      munchkinId!,
      marketoFormId!,
      (form) => {
        if (!form) return;
        form.onSuccess(() => {
          setSubmitted(true);
          return false; // prevent default redirect
        });
        mktoFormRef.current = form;
        setMktoReady(true);
      },
    );
  }

  const onSubmit = async (data: FormData) => {
    if (!mktoFormRef.current) {
      console.warn(
        "[ContactForm] Marketo form not initialised — check env vars and marketoFormId.",
      );
      return;
    }
    mktoFormRef.current.setValues(data as Record<string, unknown>).submit();
  };

  if (submitted) {
    return (
      <div
        className={cn(
          boxed ? "pt-4 md:p-8 md:rounded-2xl md:border" : "p-8",
          scheme?.bg,
          scheme?.body,
        )}
      >
        <Separator className="bg-neutral-cool md:hidden" />

        <div className="flex flex-col items-center gap-3 py-8 text-center">
          <Image
            src="/circle-check-teal.svg"
            alt=""
            width={48}
            height={48}
            loading="eager"
          />
          <p className={cn("text-heading-sm font-semibold", scheme?.heading)}>
            {`You're all set!`}
          </p>
          <p className={cn("text-body-sm max-w-[456px]", scheme?.bodyMuted)}>
            {successMessage ??
              `Your information was submitted successfully. A member of our team will be in touch with you soon.`}
          </p>
        </div>
      </div>
    );
  }

  return (
    <>
      {/* Invisible Marketo form target — must be in DOM before the script fires. */}
      {canLoadMarketo && (
        <form id={`mktoForm_${marketoFormId}`} style={{ display: "none" }} />
      )}
      {canLoadMarketo && (
        <Script
          id="marketo-forms2"
          src={`${baseUrl}/js/forms2/js/forms2.min.js`}
          strategy="lazyOnload"
          onLoad={initMarketo}
        />
      )}

      <div
        className={cn(
          boxed ? "pt-4 md:p-8 md:rounded-2xl md:border" : "p-8",
          scheme?.bg,
          scheme?.body,
        )}
      >
        {!canLoadMarketo && process.env.NODE_ENV === "development" && (
          <p className="text-body-xs text-amber-600 pb-4">
            ⚠ Marketo not configured — set NEXT_PUBLIC_MARKETO_BASE_URL,
            NEXT_PUBLIC_MARKETO_MUNCHKIN_ID, and marketoFormId.
          </p>
        )}
        <form onSubmit={handleSubmit(onSubmit)} noValidate>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {resolvedFields.map((field) => {
              const { ref: rhfRef, ...rhfProps } = register(
                field.marketoName as keyof FormData & string,
              );
              return (
                <div key={field.marketoName} className="space-y-1.5">
                  <Label
                    htmlFor={field.marketoName}
                    className="text-body-sm font-semibold"
                  >
                    {field.label}
                  </Label>
                  <input
                    id={field.marketoName}
                    ref={rhfRef}
                    type={field.type ?? "text"}
                    placeholder={field.placeholder}
                    {...rhfProps}
                    aria-invalid={!!errors[field.marketoName]}
                    className="h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-2.5 py-1 text-base shadow-xs transition-[color,box-shadow] outline-none placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50 aria-invalid:border-destructive aria-invalid:ring-3 aria-invalid:ring-destructive/20 md:text-sm"
                  />
                  {errors[field.marketoName] && (
                    <p className="text-body-xs text-destructive">
                      {String(errors[field.marketoName]?.message)}
                    </p>
                  )}
                </div>
              );
            })}
          </div>
          <Button
            type="submit"
            disabled={isSubmitting || (canLoadMarketo && !mktoReady)}
            className="mt-6"
          >
            {isSubmitting ? "Sending…" : ctaLabel}
          </Button>
        </form>
      </div>
    </>
  );
}

export default ContactForm;
